/**
 * The hosted BunnyQuery widget page: read the version a project is serving, and
 * rewrite that page to the current one.
 *
 * A BunnyQuery project serves its chat at `<subdomain>.<hostDomain>/index.html`.
 * That page is rendered from `infra/bq-template.html`, whose UNRENDERED copy is
 * published to the storage CDN as `bq/widget/latest/template.html` by
 * `infra/_bunnyquery_widget_update.py`. The same key is what
 * `admin/admin/create_service` downloads when it builds the page for a brand new
 * project, so reading it here puts the dashboard's Update Widget action on
 * exactly the file new projects are born with, rather than inventing a second
 * source of truth that could drift from it.
 *
 * Three things about that arrangement drive the code below:
 *
 *  1. The template carries two fill-in slots and nothing else that is
 *     per-project: `const __PROJECT_ID__ = ...;` and `const __ETC__ = ...;`.
 *     They are matched by NAME with the value left open (the same anchored
 *     regexes the python uses) so an ALREADY-RENDERED page can be re-rendered
 *     without accumulating edits.
 *
 *  2. The widget version is not stored anywhere per service. Neither the service
 *     record (service-settings, service_opt and conf_service are all strict
 *     allowlists) nor the host directory listing (`{name, path, size, upl, cnt}`)
 *     has a field for it. So "which version is hosted" can only be answered by
 *     reading the hosted HTML and scraping the jsdelivr pin, which is what the
 *     python does too.
 *
 *  3. Reading it over the public host URL is unreliable: the edge stamps every
 *     host response `s-maxage=86400`, the cache policy whitelists only `?v` (and
 *     re-interprets it as an S3 version id, so it is not a cache buster), and the
 *     host distribution does not answer OPTIONS, which rules out sending a
 *     `Cache-Control` request header. We presign a direct S3 GET instead, which
 *     bypasses CloudFront entirely and always sees the true current object.
 */

import { hostDomain, storageCdnDomain, widgetEtcLiteral } from '@/code/service';

/** Where the unrendered template is published. Mirrors the python constants. */
const TEMPLATE_PATH = 'bq/widget/latest/template.html';

/** The hosted page's key inside the project's subdomain folder. */
export const WIDGET_INDEX_FILE = 'index.html';

/**
 * The widget build a page loads, scraped from its jsdelivr pins. Same pattern as
 * `WIDGET_VERSION_RE` in infra/_bunnyquery_widget_update.py, and deliberately
 * so: if the two disagree about what "the hosted version" is, the dashboard and
 * the sweep script would report different numbers for the same file.
 */
const WIDGET_VERSION_RE = /bunnyquery@([0-9][0-9A-Za-z.+-]*)\//;

/**
 * The two const declarations the template carries. Matched by NAME with the
 * value left open, so the template can ship with any placeholder ("" / null) and
 * a rendered page can be re-rendered without stacking edits.
 */
const PROJECT_ID_DECL = /^([ \t]*const[ \t]+__PROJECT_ID__[ \t]*=[ \t]*).*?(;[ \t]*)$/m;
const ETC_DECL = /^([ \t]*const[ \t]+__ETC__[ \t]*=[ \t]*).*?(;[ \t]*)$/m;
/**
 * Which MCP server the hosted page talks to. OPTIONAL, unlike the two above: a
 * template published before this const existed has no `__DEV__` line, and Update
 * Widget must still work against it. Such a page falls back to its own hostname
 * (dev hosting is *.skapi.app), which resolves correctly on its own.
 */
const DEV_DECL = /^([ \t]*const[ \t]+__DEV__[ \t]*=[ \t]*).*?(;[ \t]*)$/m;
/**
 * `null` rather than `false` on prod, matching the infra renderers: the template
 * reads `__DEV__ === true || <hostname is skapi.app>`, so null leaves the
 * hostname check as the deciding signal instead of overriding it.
 */
const DEV_LITERAL = hostDomain === 'skapi.app' ? 'true' : 'null';

/** Cap on what we will treat as a widget page. The python uses the same 256KB. */
const MAX_TEMPLATE_BYTES = 256 * 1024;

export class WidgetUpdateError extends Error {
    constructor(message: string) {
        super(message);
        this.name = 'WidgetUpdateError';
    }
}

/** The widget version a page pins, or null when it pins none. */
export function parseWidgetVersion(html: string): string | null {
    const m = html ? html.match(WIDGET_VERSION_RE) : null;
    return m ? m[1] : null;
}

/**
 * Fetch the published template. Unauthenticated and CORS-open (the storage
 * distribution answers `Access-Control-Allow-Origin: *`), so this is a plain
 * fetch with no skapi involvement.
 *
 * A 403 here is the normal shape of "nothing has been published for this
 * environment yet" (S3 returns AccessDenied, not NoSuchKey, for a missing object
 * the caller may not list), so it gets its own message: the fix is to run the
 * publish step of the infra script, not to retry.
 */
export async function fetchLatestWidgetTemplate(): Promise<{ html: string; version: string | null }> {
    const url = `${storageCdnDomain}/${TEMPLATE_PATH}`;

    let res: Response;
    try {
        res = await fetch(url);
    } catch (err: any) {
        throw new WidgetUpdateError(
            'Could not reach the widget template. Check your connection and try again.',
        );
    }

    if (res.status === 403 || res.status === 404) {
        throw new WidgetUpdateError(
            'No widget template has been published for this environment yet.',
        );
    }
    if (!res.ok) {
        throw new WidgetUpdateError(`Could not read the widget template (HTTP ${res.status}).`);
    }

    const html = await res.text();
    if (!html || html.length > MAX_TEMPLATE_BYTES) {
        throw new WidgetUpdateError('The published widget template looks wrong. Nothing was changed.');
    }
    // Refuse a template we cannot fill in, rather than uploading a page whose
    // project id would stay empty and whose chat would never connect. Same
    // refusal create_service makes before it writes a new project's page.
    if (!PROJECT_ID_DECL.test(html) || !ETC_DECL.test(html)) {
        throw new WidgetUpdateError(
            'The published widget template is missing its project placeholders. Nothing was changed.',
        );
    }

    return { html, version: parseWidgetVersion(html) };
}

/**
 * Fill the template's two slots for one project. `$` is escaped in the injected
 * values because String.replace reads `$1`, `$&` and friends in the REPLACEMENT
 * string; a project id is base64-ish and an etc literal is JSON, so neither is
 * guaranteed to be `$`-free.
 */
export function renderWidgetTemplate(html: string, projectId: string): string {
    if (!projectId) throw new WidgetUpdateError('This project has no project id yet.');

    const lit = (v: string) => v.replace(/\$/g, '$$$$');

    return html
        .replace(PROJECT_ID_DECL, `$1${lit(JSON.stringify(projectId))}$2`)
        .replace(ETC_DECL, `$1${lit(widgetEtcLiteral)}$2`)
        .replace(DEV_DECL, `$1${DEV_LITERAL}$2`);
}

/**
 * Read the project's currently hosted page.
 *
 * Returns `{ hosted: false }` when the project has no index.html rather than
 * throwing: a project whose page was never built (or was deleted) is a normal
 * state the Settings row has to render, not an error.
 *
 * `service` is a `Service` instance from code/service.ts; typed loosely to keep
 * this module free of a circular import back into that file.
 */
export async function readHostedWidget(
    service: any,
): Promise<{ hosted: boolean; version: string | null }> {
    const sd = service?.service?.subdomain;
    // A subdomain in transit carries a status flag ('+new' / '*old' / '!old');
    // there is nothing to read behind one of those.
    if (!sd || '+*!'.includes(sd[0])) return { hosted: false, version: null };

    let signed: { url: string };
    try {
        signed = await service.getTemporaryUrl({
            request: 'get-host',
            path: WIDGET_INDEX_FILE,
            expires: 60,
        });
    } catch (err: any) {
        // "I could not check" is not "there is no page". An expired session, a
        // throttled admin API or a network blip all land here, and reporting any
        // of them as the definitive "No page published yet" tells the owner of a
        // perfectly good widget that it was never published. Raise it so the row
        // shows the reason instead.
        throw new WidgetUpdateError(
            'Could not check the hosted page. ' + (errText(err) || 'Try again in a moment.'),
        );
    }

    let res: Response;
    try {
        res = await fetch(signed.url);
    } catch (err: any) {
        throw new WidgetUpdateError('Could not read the hosted page. Check your connection.');
    }

    // 403/404 from the presigned GET is a missing object, which is the same
    // "no page hosted" state as a failed presign.
    if (res.status === 403 || res.status === 404) return { hosted: false, version: null };
    if (!res.ok) throw new WidgetUpdateError(`Could not read the hosted page (HTTP ${res.status}).`);

    const html = await res.text();
    return { hosted: true, version: parseWidgetVersion(html) };
}

/**
 * Write the rendered template over the project's index.html, then ask for a CDN
 * invalidation.
 *
 * The refresh is NOT optional. The host distribution serves an object for up to
 * 24h (`s-maxage=86400`), and the automatic invalidation on the hostdir stream
 * only fires when a file's SIZE changes, so a same-size rewrite would otherwise
 * never reach a visitor. Its failure is reported but does not fail the update:
 * the new bytes are already in the bucket at that point, and a refresh can be
 * retried from the Hosting page.
 */
export async function updateHostedWidget(
    service: any,
): Promise<{ version: string | null; refreshed: boolean }> {
    const sd = service?.service?.subdomain;
    if (!sd || '+*!'.includes(sd[0])) {
        throw new WidgetUpdateError('This project has no subdomain to publish to yet.');
    }

    const { html, version } = await fetchLatestWidgetTemplate();
    const rendered = renderWidgetTemplate(html, service.service.projectId);

    // uploadHostFiles takes a FormData/form and reads File entries off it; the
    // destination key is built server side as `<subdomain>/<file name>`. Same
    // synthetic-form shape hosting.vue's 404-page upload uses.
    const file = new File([rendered], WIDGET_INDEX_FILE, { type: 'text/html; charset=utf-8' });
    const form = new FormData();
    form.append('file', file);

    const up = await service.uploadHostFiles(form, {
        target: 'host',
        contentTypeMapping: { [WIDGET_INDEX_FILE]: 'text/html; charset=utf-8' },
    });

    if (!up?.completed?.length) {
        const cause = up?.failedErrors?.[0];
        throw new WidgetUpdateError(
            'The page could not be uploaded.' + (cause ? ' ' + errText(cause) : ''),
        );
    }

    let refreshed = false;
    try {
        await service.refreshCDN();
        refreshed = true;
    } catch (err: any) {
        refreshed = false;
    }

    return { version, refreshed };
}

/** Best-effort one-liner out of whatever a call rejected with. */
function errText(err: any): string {
    if (!err) return '';
    if (typeof err === 'string') return err;
    if (typeof err?.message === 'string') return err.message;
    return '';
}

/** Best-effort one-liner out of whatever the S3 POST rejected with. */
